import React, { useState, useMemo } from 'react';
import { View, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import RemoteImage from 'src/presentation/components/molecules/RemoteImage';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import Translations from 'src/core/utils/Translations';
import type { ApiContentBlockVideo } from 'src/core/models/api/ApiContentBlock';
import type { ContentBlockVideoProps } from 'src/presentation/components/organisms/contentblocks/types/ContentBlockTypes';

/**
 * Web sibling of ContentBlockVideo. Native plays through react-native-youtube-iframe and
 * react-native-webview, which have no web implementation here, so the browser gets the
 * provider's own embed in a DOM iframe instead. Poster, play gesture and the choice between
 * YouTube and Vimeo follow the native component.
 */

type VideoData = ApiContentBlockVideo['data'];

const VIDEO_HEIGHT = 226;

// Plain CSS: the iframe is a DOM node, so StyleSheet.create ids mean nothing to it.
const iframeStyle = { border: 0, width: '100%', height: '100%' };

function getPosterFromData(d: VideoData): string {
	if (d.cb_video_placeholder === 'custom' && d.cb_custom_image) {
		return d.cb_custom_image;
	}
	if (d.cb_video_type === 'youtube' && d.cb_yt_video_code) {
		return `https://img.youtube.com/vi/${d.cb_yt_video_code}/hqdefault.jpg`;
	}
	return d.cb_generated_image ?? '';
}

/** Poster + spinner shown on top of the player until the video is ready, so there is no blank white frame. */
function LoadingOverlay({ posterUri }: { posterUri: string }) {
	return (
		<View pointerEvents="none" style={styles.loadingOverlay}>
			<RemoteImage style={StyleSheet.absoluteFill} uri={posterUri} />
			<View style={styles.scrim} />
			<ActivityIndicator color={Colors.white} size="large" />
		</View>
	);
}

export default function ContentBlockVideo({ data }: ContentBlockVideoProps) {
	const contentWidth = useContentWidth();
	const [playing, setPlaying] = useState(false);
	const [ready, setReady] = useState(false);

	// A plain object, not StyleSheet.create: a sheet built inside useMemo reads as unnamed to
	// react-native/no-unused-styles, which then flags every rule in it.
	const sizedStyles = useMemo(
		() => ({
			container: {
				width: contentWidth,
			},
			poster: {
				width: contentWidth,
			},
			player: {
				width: contentWidth,
			},
		}),
		[contentWidth],
	);

	const videoData = data.data;
	const posterUri = getPosterFromData(videoData);
	// Same discriminator as native: the presence of a YouTube code decides, not cb_video_type.
	const isYoutube = !!videoData.cb_yt_video_code;
	const embedUrl = isYoutube
		? `https://www.youtube.com/embed/${videoData.cb_yt_video_code ?? ''}?autoplay=1`
		: `https://player.vimeo.com/video/${videoData.cb_vimeo_video_code ?? ''}?title=0&byline=0&portrait=0&autoplay=1`;

	if (!playing) {
		return (
			<View style={[styles.container, sizedStyles.container]}>
				<TouchableOpacity activeOpacity={0.85} style={[styles.poster, sizedStyles.poster]} onPress={() => setPlaying(true)}>
					<RemoteImage style={StyleSheet.absoluteFill} uri={posterUri} />
					<View style={styles.playButton}>
						<Ionicons name="play" size={34} style={styles.playIcon} />
					</View>
				</TouchableOpacity>
			</View>
		);
	}

	return (
		<View style={[styles.container, sizedStyles.container]}>
			<View style={[styles.player, sizedStyles.player]}>
				<iframe
					allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
					allowFullScreen={true}
					src={embedUrl}
					style={iframeStyle}
					title={Translations.labelVideoPlayer}
					onLoad={() => setReady(true)}
				/>
				{!ready ? <LoadingOverlay posterUri={posterUri} /> : null}
			</View>
		</View>
	);
}

const styles = StyleSheet.create({
	container: {
		paddingVertical: Dimens.padding8,
	},
	poster: {
		height: VIDEO_HEIGHT,
		justifyContent: 'center',
		alignItems: 'center',
	},
	player: {
		height: VIDEO_HEIGHT,
	},
	playButton: {
		width: 72,
		height: 72,
		borderRadius: 36,
		backgroundColor: Colors.secondary,
		justifyContent: 'center',
		alignItems: 'center',
		shadowColor: Colors.black,
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.3,
		shadowRadius: 6,
	},
	playIcon: {
		color: Colors.white,
		// Optical centering: the play triangle looks too far left when centered geometrically.
		marginLeft: 4,
	},
	loadingOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		justifyContent: 'center',
		alignItems: 'center',
	},
	scrim: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: Colors.overlay30,
	},
});
